import isEqual from 'lodash/isEqual';
import { actionTypes as FirebaseActions } from 'react-redux-firebase';

import {
  BUILDER_PAGE_HISTORY_LIMIT,
  EMPTY_EMAIL_TEMPLATE,
  EMPTY_NOTE_TEMPLATE,
  INITIAL_TEMPLATE_BUILDER_VALUES,
} from '../../constants/templates';
import {
  ClaimLinkEditorPageEnum,
  IClaimLinkBuilderValues,
  IEmailTemplate,
  INoteTemplate,
  ITemplatesState,
  TemplateValidationModalEnum,
} from '../../types/templates';
import { isTemplateEmail, mergePages, withSyncedDeprecatedFields } from '../../utils/templates';
import * as AuthActions from '../actions/auth';
import * as TemplatesActions from '../actions/templates';

const createInitialPageHistory = (): ITemplatesState['claimLinks']['pageHistory'] => ({
  [ClaimLinkEditorPageEnum.PickYourGift]: { past: [], future: [] },
  [ClaimLinkEditorPageEnum.ProductOptions]: { past: [], future: [] },
  [ClaimLinkEditorPageEnum.ShippingDetails]: { past: [], future: [] },
});

const getDraftSnapshot = (draft: IClaimLinkBuilderValues): string => {
  const { activePageId: _activePageId, ...rest } = draft;
  return JSON.stringify(rest);
};

export const initialState: ITemplatesState = {
  emails: {
    templates: null,
    isLoading: false,
    error: null,
  },
  notes: {
    templates: null,
    isLoading: false,
    error: null,
  },
  claimLinks: {
    templates: null,
    isLoading: false,
    isSilentLoading: false,
    error: null,
    totalCount: 0,
    draft: null,
    draftSnapshot: null,
    validationErrors: null,
    validationErrorMessage: null,
    validationModal: TemplateValidationModalEnum.None,
    pageHistory: createInitialPageHistory(),
    lastFetchParams: null,
    currentTemplate: null,
    isCurrentTemplateLoading: false,
    isSaving: false,
  },
  templateDetails: {
    template: null,
    isLoading: false,
    error: null,
  },
};

const reducer = (state: ITemplatesState = initialState, action: any): ITemplatesState => {
  switch (action.type) {
    case TemplatesActions.FETCH_EMAILS_TEMPLATES_REQUEST: {
      return {
        ...state,
        emails: {
          ...state.emails,
          isLoading: true,
          error: null,
        },
      };
    }
    case TemplatesActions.FETCH_EMAILS_TEMPLATES_FAILURE: {
      return {
        ...state,
        emails: { ...state.emails, isLoading: false, error: action.payload },
      };
    }
    case TemplatesActions.FETCH_EMAILS_TEMPLATES_SUCCESS: {
      const { email_templates } = action.payload || {};
      return {
        ...state,
        emails: {
          templates: [...(email_templates && email_templates.length ? email_templates : []), EMPTY_EMAIL_TEMPLATE],
          isLoading: false,
          error: null,
        },
      };
    }
    case TemplatesActions.FETCH_NOTES_TEMPLATES_REQUEST: {
      return {
        ...state,
        notes: { ...state.notes, isLoading: true, error: null },
      };
    }
    case TemplatesActions.FETCH_NOTES_TEMPLATES_FAILURE: {
      return {
        ...state,
        notes: { ...state.notes, isLoading: false, error: action.payload },
      };
    }
    case TemplatesActions.FETCH_NOTES_TEMPLATES_SUCCESS: {
      const { message_templates } = action.payload || {};
      return {
        ...state,
        notes: {
          templates: [...(message_templates && message_templates.length ? message_templates : []), EMPTY_NOTE_TEMPLATE],
          isLoading: false,
          error: null,
        },
      };
    }
    case TemplatesActions.FETCH_CLAIM_LINK_TEMPLATES_REQUEST: {
      const { silent, resolve, reject, ...fetchParams } = action.payload || {};
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          isLoading: silent ? state.claimLinks.isLoading : true,
          isSilentLoading: silent ? true : state.claimLinks.isSilentLoading,
          error: null,
          lastFetchParams: fetchParams,
        },
      };
    }
    case TemplatesActions.FETCH_CLAIM_LINK_TEMPLATES_FAILURE: {
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          templates: null,
          isLoading: false,
          isSilentLoading: false,
          error: action.payload,
          totalCount: 0,
        },
      };
    }
    case TemplatesActions.FETCH_CLAIM_LINK_TEMPLATES_SUCCESS: {
      const { claim_page_experiences, total_count } = action.payload || {};
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          templates: claim_page_experiences || [],
          isLoading: false,
          isSilentLoading: false,
          error: null,
          totalCount: total_count || 0,
        },
      };
    }
    case TemplatesActions.UPDATE_CLAIM_LINK_TEMPLATE_DETAILS_SUCCESS: {
      const { uid, status, is_default, name, department_ids } = action.payload || {};
      const templates = state.claimLinks.templates || [];

      const updatedTemplates = templates.map((template) => {
        if (template.uid === uid) {
          return {
            ...template,
            ...(status ? { status } : {}),
            ...(is_default !== undefined ? { is_default } : {}),
            ...(name ? { name } : {}),
            ...(department_ids ? { department_ids } : {}),
          };
        }
        // clear is_default on all other templates when a new default is set
        if (is_default) {
          return { ...template, is_default: false };
        }
        return template;
      });

      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          error: null,
          templates: updatedTemplates,
        },
      };
    }
    case TemplatesActions.EDIT_CLAIM_LINK_TEMPLATE_SUCCESS: {
      const updatedTemplate = action.payload;
      const templates = state.claimLinks.templates || [];
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          templates: templates.map((t) => (t.uid === updatedTemplate?.uid ? updatedTemplate : t)),
        },
      };
    }
    case TemplatesActions.ADD_TEMPLATE_VALUE: {
      return {
        ...state,
        templateDetails: {
          ...state.templateDetails,
          template: {
            ...state.templateDetails.template,
            ...action.payload,
          },
        },
      };
    }
    case TemplatesActions.SET_TEMPLATE_VALUE: {
      return {
        ...state,
        templateDetails: {
          ...state.templateDetails,
          template: action.payload,
        },
      };
    }
    case TemplatesActions.EDIT_TEMPLATE_REQUEST:
    case TemplatesActions.ADD_TEMPLATE_REQUEST: {
      return {
        ...state,
        templateDetails: {
          template: action.payload.template,
          isLoading: true,
          error: null,
        },
      };
    }
    case TemplatesActions.EDIT_TEMPLATE_SUCCESS:
    case TemplatesActions.ADD_TEMPLATE_SUCCESS: {
      return {
        ...state,
        templateDetails: {
          ...state.templateDetails,
          isLoading: false,
        },
      };
    }
    case TemplatesActions.EDIT_TEMPLATE_FAILURE:
    case TemplatesActions.ADD_TEMPLATE_FAILURE: {
      return {
        ...state,
        templateDetails: {
          ...state.templateDetails,
          isLoading: false,
          error: action.payload,
        },
      };
    }
    case TemplatesActions.DELETE_TEMPLATE_SUCCESS: {
      const { itemId, type } = action.payload;
      const filteredList = [...((isTemplateEmail(type) ? state.emails.templates : state.notes.templates) || [])].filter(
        (template) => template.item_id !== itemId,
      );

      return {
        ...state,
        ...(isTemplateEmail(type)
          ? {
              emails: {
                ...state.emails,
                templates: filteredList as IEmailTemplate[],
              },
            }
          : {
              notes: {
                ...state.notes,
                templates: filteredList as INoteTemplate[],
              },
            }),
      };
    }
    case TemplatesActions.CLEAR_TEMPLATE_VALUE: {
      return {
        ...state,
        templateDetails: {
          template: null,
          isLoading: false,
          error: null,
        },
      };
    }
    case TemplatesActions.SET_CLAIM_LINK_BUILDER_DRAFT: {
      const draft = action.payload ? withSyncedDeprecatedFields(action.payload) : null;
      const draftSnapshot = draft ? getDraftSnapshot(draft) : null;

      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          draft,
          draftSnapshot,
          validationErrors: null,
          validationErrorMessage: null,
          validationModal: TemplateValidationModalEnum.None,
          pageHistory: createInitialPageHistory(),
        },
      };
    }
    case TemplatesActions.UPDATE_CLAIM_LINK_BUILDER_FIELD: {
      const currentDraft = state.claimLinks.draft || INITIAL_TEMPLATE_BUILDER_VALUES;

      const rawUpdates = action.payload as Partial<IClaimLinkBuilderValues>;

      // When globalLogoImageUrl changes, keep non-customized pages in sync (override stays undefined)
      let resolvedPages = rawUpdates.pages ? mergePages(currentDraft.pages, rawUpdates.pages) : undefined;
      if (rawUpdates.globalLogoImageUrl !== undefined) {
        const base = resolvedPages ?? currentDraft.pages;
        const syncedPages = { ...base };
        (Object.keys(syncedPages) as ClaimLinkEditorPageEnum[]).forEach((pageId) => {
          const page = syncedPages[pageId];
          // Pages using the default have undefined override — no action needed,
          // they automatically pick up the new global. Only clear overrides that
          // matched the old global (legacy data) so they fall back to the new global.
          if (page.overrides.logoImageUrl === currentDraft.globalLogoImageUrl) {
            syncedPages[pageId] = {
              ...page,
              overrides: { ...page.overrides, logoImageUrl: undefined },
            };
          }
        });
        resolvedPages = syncedPages as typeof currentDraft.pages;
      }

      const updates: Partial<IClaimLinkBuilderValues> = withSyncedDeprecatedFields({
        ...rawUpdates,
        ...(resolvedPages !== undefined ? { pages: resolvedPages } : {}),
      });

      let pageHistory = { ...state.claimLinks.pageHistory };
      if (rawUpdates.pages) {
        (Object.keys(rawUpdates.pages) as ClaimLinkEditorPageEnum[]).forEach((pageId) => {
          const prevPage = currentDraft.pages[pageId];
          const newPage = (updates.pages as any)?.[pageId];
          const existing = pageHistory[pageId];

          if (isEqual(prevPage, newPage)) {
            return;
          }

          pageHistory = {
            ...pageHistory,
            [pageId]: {
              past: [...existing.past, prevPage].slice(-BUILDER_PAGE_HISTORY_LIMIT),
              future: [],
            },
          };
        });
      }

      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          pageHistory,
          draft: {
            ...currentDraft,
            ...updates,
          },
        },
      };
    }
    case TemplatesActions.UNDO_TEMPLATE_BUILDER_CHANGE: {
      const pageId = action.payload as ClaimLinkEditorPageEnum;
      const currentDraft = state.claimLinks.draft || INITIAL_TEMPLATE_BUILDER_VALUES;
      const { past, future } = state.claimLinks.pageHistory[pageId];
      if (!past.length) {
        return state;
      }

      const previous = past[past.length - 1];
      const currentPage = currentDraft.pages[pageId];

      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          pageHistory: {
            ...state.claimLinks.pageHistory,
            [pageId]: {
              past: past.slice(0, -1),
              future: [currentPage, ...future],
            },
          },
          draft: {
            ...currentDraft,
            pages: { ...currentDraft.pages, [pageId]: previous },
          },
        },
      };
    }
    case TemplatesActions.REDO_TEMPLATE_BUILDER_CHANGE: {
      const pageId = action.payload as ClaimLinkEditorPageEnum;
      const currentDraft = state.claimLinks.draft || INITIAL_TEMPLATE_BUILDER_VALUES;
      const { past, future } = state.claimLinks.pageHistory[pageId];
      if (!future.length) {
        return state;
      }

      const next = future[0];
      const currentPage = currentDraft.pages[pageId];

      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          pageHistory: {
            ...state.claimLinks.pageHistory,
            [pageId]: {
              past: [...past, currentPage],
              future: future.slice(1),
            },
          },
          draft: {
            ...currentDraft,
            pages: { ...currentDraft.pages, [pageId]: next },
          },
        },
      };
    }
    case TemplatesActions.UPDATE_ACTIVE_PAGE: {
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          draft: {
            ...(state.claimLinks.draft || INITIAL_TEMPLATE_BUILDER_VALUES),
            activePageId: action.payload,
          },
        },
      };
    }
    case TemplatesActions.SET_CLAIM_LINK_TEMPLATE_SAVING: {
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          isSaving: action.payload,
        },
      };
    }
    case TemplatesActions.FETCH_CLAIM_LINK_TEMPLATE_BY_ID_REQUEST: {
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          isCurrentTemplateLoading: true,
          currentTemplate: null,
        },
      };
    }
    case TemplatesActions.FETCH_CLAIM_LINK_TEMPLATE_BY_ID_FAILURE: {
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          isCurrentTemplateLoading: false,
        },
      };
    }
    case TemplatesActions.FETCH_CLAIM_LINK_TEMPLATE_BY_ID_SUCCESS: {
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          isCurrentTemplateLoading: false,
          currentTemplate: action.payload,
        },
      };
    }
    case TemplatesActions.CLEAR_CLAIM_LINK_BUILDER_DRAFT: {
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          draft: null,
          draftSnapshot: null,
          validationErrors: null,
          validationErrorMessage: null,
          validationModal: TemplateValidationModalEnum.None,
          pageHistory: createInitialPageHistory(),
          currentTemplate: null,
        },
      };
    }
    case TemplatesActions.SET_TEMPLATE_VALIDATION_ERRORS: {
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          validationErrors: action.payload.errors,
          validationErrorMessage: action.payload.message,
        },
      };
    }
    case TemplatesActions.SET_TEMPLATE_VALIDATION_MODAL: {
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          validationModal: action.payload,
        },
      };
    }
    case TemplatesActions.CLEAR_TEMPLATE_VALIDATION: {
      return {
        ...state,
        claimLinks: {
          ...state.claimLinks,
          validationErrors: null,
          validationErrorMessage: null,
          validationModal: TemplateValidationModalEnum.None,
        },
      };
    }

    case FirebaseActions.LOGOUT:
    case AuthActions.ADMIN_ROLE_CHANGED:
    case AuthActions.CHANGE_ORGANIZATION_SUCCESS: {
      return initialState;
    }
    default:
      return state;
  }
};

export default reducer;
